import { useEffect, useState } from 'react';
import { authedFetch, type SessionState } from '../lib/session.js';

interface CheckResult {
  type: string;
  status: 'green' | 'yellow' | 'red';
  details: Record<string, unknown> & { checked_at?: string };
}

// Friendly labels. Things Joan said he doesn't recognize (Postgres, GHL API,
// Cron) are masked behind business language. Lusha is hidden completely until
// the team uses it again.
const LABELS: Record<string, string> = {
  db:     'Base de datos',
  ghl:    'Sincronización GHL',
  zoho:   'Sincronización Zoho',
  cron:   'Actualizaciones automáticas',
  poller: 'Actualizaciones automáticas',
};
// Hide:
//  - lusha: AT no usa Lusha activamente (decisión Joan 2026-05-25)
//  - poller: legacy check replaced by 'cron'; old rows in DB still show until purged
const HIDDEN_TYPES = new Set(['lusha', 'poller']);

export function HealthFooter({ session }: { session: SessionState }) {
  const [checks, setChecks] = useState<CheckResult[] | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;
    async function load() {
      try {
        const r = await authedFetch(session, '/api/dashboard/health');
        if (!r.ok) return;
        const j = (await r.json()) as { checks: CheckResult[] };
        if (!cancelled) setChecks(j.checks.filter((c) => !HIDDEN_TYPES.has(c.type)));
      } catch { /* ignore */ }
    }
    void load();
    timer = setInterval(load, 60_000);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [session.token]);

  if (!checks || checks.length === 0) return null;
  const worst: 'green' | 'yellow' | 'red' = checks.some((c) => c.status === 'red') ? 'red'
    : checks.some((c) => c.status === 'yellow') ? 'yellow' : 'green';
  const dotColor = worst === 'green' ? '#16a34a' : worst === 'yellow' ? '#b45309' : '#dc2626';
  const summary = worst === 'green'
    ? 'Todo funciona'
    : checks.filter((c) => c.status !== 'green').map((c) => LABELS[c.type] ?? c.type).join(', ');

  return (
    <div style={{ marginTop: 24, position: 'relative' }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: 10, color: 'var(--fg-subtle)', cursor: 'pointer',
          padding: '4px 8px', borderRadius: 4,
        }}
        title="Haz clic para ver el estado de las integraciones"
      >
        <span style={{ width: 6, height: 6, borderRadius: 3, background: dotColor, display: 'inline-block' }} />
        {summary}
      </div>
      {expanded && (
        <div style={{
          position: 'absolute', bottom: '100%', left: 0, marginBottom: 6, zIndex: 50,
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
          padding: 10, boxShadow: '0 4px 12px rgba(17,24,39,.1)', minWidth: 320,
        }} onClick={(e) => e.stopPropagation()}>
          <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
            <tbody>
              {checks.map((c) => {
                const color = c.status === 'green' ? '#16a34a' : c.status === 'yellow' ? '#b45309' : '#dc2626';
                return (
                  <tr key={c.type} style={{ borderBottom: '1px solid var(--rule)' }}>
                    <td style={{ padding: '4px 8px', color: 'var(--fg)' }}>{LABELS[c.type] ?? c.type}</td>
                    <td style={{ padding: '4px 8px' }}>
                      <span style={{ width: 6, height: 6, borderRadius: 3, background: color, display: 'inline-block', marginRight: 4 }} />
                      {c.status === 'green' ? 'OK' : c.status === 'yellow' ? 'atención' : 'caído'}
                    </td>
                    <td style={{ padding: '4px 8px', fontSize: 9, color: 'var(--fg-subtle)' }}>
                      {(c.details.checked_at as string | undefined)?.slice(0, 16).replace('T', ' ')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
